import { Trans, t } from "@lingui/macro";
import cx from "classnames";
import { useCallback, useState } from "react";

import Modal from "components/Modal/Modal";
import { shortenAddress } from "lib/legacy";

import type { TraderProfile } from "domain/copyTrading/types";
import { TraderAvatar } from "./TraderAvatar";

type Props = {
  isVisible: boolean;
  trader: TraderProfile | null;
  onClose: () => void;
  onConfirm: (trader: TraderProfile, collateralPerTrade: string, maxAllocation: string) => void;
  isSubmitting: boolean;
};

function FeeInfoRow({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="CT-modal-row">
      <span className="CT-modal-row__label">{label}</span>
      <span className={cx("CT-modal-row__value", { "CT-positive": highlight })}>{value}</span>
    </div>
  );
}

export function FollowModal({ isVisible, trader, onClose, onConfirm, isSubmitting }: Props) {
  const [collateral, setCollateral] = useState("");
  const [maxAlloc, setMaxAlloc] = useState("");

  const handleConfirm = useCallback(() => {
    if (!trader || !collateral || Number(collateral) <= 0) return;
    onConfirm(trader, collateral, maxAlloc || "0");
  }, [trader, collateral, maxAlloc, onConfirm]);

  if (!trader) return null;

  const feeRatePct = (trader.copyFeeRate / 100).toFixed(2);
  const exampleFee =
    collateral && Number(collateral) > 0
      ? ((Number(collateral) * trader.copyFeeRate) / 10000).toFixed(2)
      : "—";

  return (
    <Modal
      isVisible={isVisible}
      setIsVisible={onClose}
      label={<Trans>Follow Trader</Trans>}
      className="CT-follow-modal"
      qa="follow-modal"
    >
      {/* Trader header */}
      <div className="CT-modal-trader-header">
        <TraderAvatar
          address={trader.address}
          avatarUrl={trader.avatarUrl}
          name={trader.displayName}
          size={48}
          className="CT-modal-trader-avatar"
        />
        <div className="CT-modal-trader-info">
          {trader.displayName && <div className="CT-modal-trader-name">{trader.displayName}</div>}
          <div className="CT-modal-trader-address">{shortenAddress(trader.address, 12)}</div>
          <div className="CT-modal-trader-stats">
            {trader.winRate !== undefined && (
              <span className="CT-badge CT-badge--green">{trader.winRate.toFixed(0)}% win</span>
            )}
            {trader.pnlPercent30d !== undefined && (
              <span className={cx("CT-badge", trader.pnlPercent30d >= 0 ? "CT-badge--green" : "CT-badge--red")}>
                {trader.pnlPercent30d >= 0 ? "+" : ""}
                {trader.pnlPercent30d.toFixed(1)}% 30d
              </span>
            )}
            <span className="CT-badge CT-badge--blue">{trader.followerCount} followers</span>
          </div>
        </div>
      </div>

      <div className="CT-modal-divider" />

      {/* Collateral input */}
      <div className="CT-modal-field">
        <label className="CT-modal-field__label">
          <Trans>BUSDC per trade</Trans>
        </label>
        <div className="CT-modal-input-wrap">
          <input
            className="CT-modal-input"
            type="number"
            placeholder="100"
            min="0"
            value={collateral}
            onChange={(e) => setCollateral(e.target.value)}
          />
          <span className="CT-modal-input-token">BUSDC</span>
        </div>
        <p className="CT-modal-field__hint">
          <Trans>Fixed collateral used for each trade this trader opens</Trans>
        </p>
      </div>

      {/* Max allocation input */}
      <div className="CT-modal-field">
        <label className="CT-modal-field__label">
          <Trans>Max total allocation</Trans>{" "}
          <span className="CT-modal-optional">
            (<Trans>optional</Trans>)
          </span>
        </label>
        <div className="CT-modal-input-wrap">
          <input
            className="CT-modal-input"
            type="number"
            placeholder={t`Unlimited`}
            min="0"
            value={maxAlloc}
            onChange={(e) => setMaxAlloc(e.target.value)}
          />
          <span className="CT-modal-input-token">BUSDC</span>
        </div>
        <p className="CT-modal-field__hint">
          <Trans>Cap on total collateral across all open copy positions for this trader</Trans>
        </p>
      </div>

      <div className="CT-modal-divider" />

      {/* Fee breakdown */}
      <div className="CT-modal-fees">
        <FeeInfoRow label={t`Leader copy fee`} value={`${feeRatePct}% per trade`} />
        <FeeInfoRow
          label={t`Fee on this trade`}
          value={collateral && Number(collateral) > 0 ? `~${exampleFee} BUSDC` : "—"}
        />
        <FeeInfoRow
          label={t`Net collateral per trade`}
          value={
            collateral && Number(collateral) > 0
              ? `~${(Number(collateral) - Number(exampleFee || 0)).toFixed(2)} BUSDC`
              : "—"
          }
          highlight
        />
      </div>

      {/* Pre-approve notice */}
      <div className="CT-modal-notice">
        <span className="CT-modal-notice__icon">ℹ</span>
        <p>
          <Trans>
            You must also approve the CopyTradingManager as a Router plugin once — a separate transaction will be
            prompted if needed.
          </Trans>
        </p>
      </div>

      <button
        className="CT-btn-primary w-full mt-16"
        onClick={handleConfirm}
        disabled={isSubmitting || !collateral || Number(collateral) <= 0}
      >
        {isSubmitting ? <Trans>Confirming...</Trans> : <Trans>Confirm Follow</Trans>}
      </button>
    </Modal>
  );
}
